import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  ChevronDownIcon,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import AffiliatePrimaryButton from "../components/AffiliatePrimaryButton";
import AffiliateSecondaryButton from "../components/AffiliateSecondaryButton";
import { AffiliateAuthContext } from "../context/AffiliateAuthContext";
import { useAffiliateCoupons } from "../hooks/useAffiliateCoupons";
import { useSendAccessCoupons, useSendRegularCoupon } from "../hooks/useAffiliateCouponSends";
import { getCommissionTierProgressParts } from "../utils/affiliateCommissionTiers";
import { isPartnerKind } from "../utils/portalKind";
import { usePortalBasePath } from "../hooks/usePortalBasePath";
import {
  COUPON_STATUS_BADGE_CLASSES,
  COUPON_STATUS_FILTER_OPTIONS,
  COUPON_STATUS_SENT,
  couponStatusLabel,
  filterCoupons,
  isUsedStatus,
  sortCouponsByStatus,
} from "../utils/couponStatus";
import {
  affiliateAccentSoftActive,
  affiliateBrandBar,
  affiliateBrandGradientText,
} from "../utils/uiClasses";

const StatCard = ({ label, value, hint, footer }) => (
  <div
    className={`relative overflow-hidden rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-5 sm:p-6 shadow-sm`}
  >
    <div className={`absolute inset-x-0 top-0 h-1 ${affiliateBrandBar}`} />
    <p className={`${typography.label} ${textColors.secondary}`}>{label}</p>
    <p className={`mt-2 ${typography.h1} ${textColors.primary} tracking-tight`}>{value}</p>
    {footer ? (
      <p className={`mt-2 ${typography.bodySmall} ${textColors.primary} font-medium`}>{footer}</p>
    ) : null}
    {hint ? <p className={`mt-2 ${typography.caption} ${textColors.muted}`}>{hint}</p> : null}
  </div>
);

const ExpandChevron = ({ isOpen }) => (
  <span
    className={`inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full border ${borders.default} ${surfaces.secondary} ${textColors.secondary} transition-transform duration-200 ease-out ${
      isOpen ? `rotate-180 ${affiliateAccentSoftActive}` : ""
    }`}
    aria-hidden="true"
  >
    <ChevronDownIcon size="sm" />
  </span>
);

/**
 * Who a coupon was emailed to. One recipient reads as the address itself; several
 * collapse to "N people" and expand on click, so the column stays narrow.
 */
const SentRecipientsCell = ({ coupon, isOpen, onToggle }) => {
  const recipients = Array.isArray(coupon.sent_recipients) ? coupon.sent_recipients : [];
  if (recipients.length === 0) {
    return <span className={`${typography.bodySmall} ${textColors.muted}`}>—</span>;
  }
  if (recipients.length === 1) {
    return (
      <span className={`${typography.bodySmall} ${textColors.secondary} break-all`}>
        {recipients[0]}
      </span>
    );
  }
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={`Show the ${recipients.length} people ${coupon.code} was sent to`}
        className={`inline-flex items-center gap-1 ${typography.bodySmall} ${textColors.secondary} underline underline-offset-2`}
      >
        {recipients.length} people
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen ? (
        <ul
          className={`mt-1 rounded-lg border ${borders.default} ${surfaces.primary} p-2 space-y-1 shadow-sm`}
          aria-label={`People ${coupon.code} was sent to`}
        >
          {recipients.map((email) => (
            <li key={email} className={`${typography.caption} ${textColors.secondary} break-all`}>
              {email}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const CouponActions = ({
  code,
  shareLink,
  onCopy,
  fullWidth = false,
  onToggleSend = null,
  isSendOpen = false,
  // A coupon already emailed to someone offers "Send again" rather than a first send.
  sendLabel = "Send by email",
}) => (
  <div className={`flex ${fullWidth ? "flex-col w-full" : "flex-wrap justify-end"} gap-2`}>
    <AffiliateSecondaryButton
      fullWidth={fullWidth}
      aria-label={`Copy coupon code ${code}`}
      title="Copy code"
      onClick={() => onCopy(code, "Copied coupon code.")}
    >
      Copy code
    </AffiliateSecondaryButton>
    {shareLink ? (
      <AffiliateSecondaryButton
        fullWidth={fullWidth}
        aria-label={`Copy share link for ${code}`}
        title="Copy link"
        onClick={() => onCopy(shareLink, "Copied link.")}
      >
        Copy link
      </AffiliateSecondaryButton>
    ) : null}
    {onToggleSend ? (
      <AffiliateSecondaryButton
        fullWidth={fullWidth}
        aria-label={`${sendLabel} for coupon ${code}`}
        aria-expanded={isSendOpen}
        title={sendLabel}
        onClick={onToggleSend}
      >
        {sendLabel}
      </AffiliateSecondaryButton>
    ) : null}
  </div>
);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const isValidEmail = (value) => EMAIL_PATTERN.test(String(value || "").trim());

const sendInputClasses = [
  "w-full px-3 py-2 rounded-lg border",
  surfaces.primary,
  borders.default,
  typography.body,
  textColors.primary,
  "focus:outline-none focus:ring-2 focus:ring-pink-400 dark:focus:ring-pink-300",
].join(" ");

/**
 * Inline "Send by email" panel attached to a single coupon.
 *
 * Deliberately inline (not a modal): the page keeps at most one panel open at a
 * time, so the email box and its feedback always belong to the coupon above it.
 */
const CouponSendPanel = ({
  coupon,
  emails,
  onEmailChange,
  onAddEmail,
  onRemoveEmail,
  onSubmit,
  isSending,
  feedback,
}) => {
  // An access coupon holds several usages, so it can go to one recipient per unsent
  // usage. Regular coupons are always one-at-a-time.
  const slots = coupon.is_access_coupon ? Math.max(1, Number(coupon.remaining_sends || 1)) : 1;
  const canAddMore = emails.length < slots;

  return (
    <div
      className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4 space-y-3 shadow-sm`}
      aria-label={`Send coupon ${coupon.code} by email panel`}
      role="group"
    >
      <p className={`${typography.label} ${textColors.secondary}`}>
        Send {coupon.code} by email
      </p>
      <form className="space-y-2" onSubmit={onSubmit}>
        {emails.map((email, index) => {
          const inputId = `coupon-send-email-${coupon.id}-${index}`;
          const label =
            emails.length === 1
              ? `Recipient email for ${coupon.code}`
              : `Recipient email ${index + 1} for ${coupon.code}`;
          return (
            <div key={inputId} className="flex items-start gap-2">
              <div className="flex-1 space-y-1">
                <label htmlFor={inputId} className="sr-only">
                  {label}
                </label>
                <input
                  id={inputId}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => onEmailChange(index, e.target.value)}
                  placeholder="name@example.com"
                  aria-label={label}
                  className={sendInputClasses}
                />
              </div>
              {emails.length > 1 ? (
                <button
                  type="button"
                  onClick={() => onRemoveEmail(index)}
                  disabled={isSending}
                  aria-label={`Remove recipient ${index + 1} for ${coupon.code}`}
                  className={`px-2 py-2 rounded-lg border ${borders.default} ${textColors.secondary} disabled:opacity-40`}
                >
                  ×
                </button>
              ) : null}
            </div>
          );
        })}

        <div className="flex flex-wrap items-center gap-2">
          {slots > 1 ? (
            <button
              type="button"
              onClick={onAddEmail}
              disabled={isSending || !canAddMore}
              aria-label={`Add another recipient for ${coupon.code}`}
              className={[
                "px-2.5 py-1 rounded-lg border",
                borders.default,
                textColors.secondary,
                typography.bodySmall,
                "disabled:opacity-40 disabled:cursor-not-allowed",
              ].join(" ")}
            >
              + Add another
            </button>
          ) : null}
          {slots > 1 ? (
            <span className={`${typography.caption} ${textColors.muted}`}>
              {emails.length} of {slots} remaining {slots === 1 ? "usage" : "usages"}
            </span>
          ) : null}
          <div className="ml-auto">
            <AffiliatePrimaryButton
              type="submit"
              size="sm"
              disabled={isSending}
              loading={isSending}
              aria-label={`Send coupon ${coupon.code}`}
            >
              Send
            </AffiliatePrimaryButton>
          </div>
        </div>
      </form>
      {feedback ? (
        <p
          role={feedback.type === "error" ? "alert" : "status"}
          aria-live="polite"
          className={`${typography.bodySmall} ${
            feedback.type === "error"
              ? "text-red-700 dark:text-red-300"
              : "text-green-700 dark:text-green-300"
          }`}
        >
          {feedback.message}
        </p>
      ) : null}
      <p className={`${typography.caption} ${textColors.muted}`}>
        We email the code straight to that person, with your name as the sender.
      </p>
    </div>
  );
};

const formatDateShort = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

/** "PRO for 62 days" / "PRO until Oct 17, 2026" — shown when the coupon grants free access. */
const formatFreeAccess = (details) => {
  const plan = String(details?.free_access_plan || "").toUpperCase();
  if (!plan) return null;
  if (details.free_access_days) return `${plan} for ${details.free_access_days} days`;
  if (details.free_access_until) return `${plan} until ${formatDateShort(details.free_access_until)}`;
  return plan;
};

/** "1/1", "0/2" — or just the used count when there is no redemption cap. */
const formatUsed = (coupon) => {
  const used = coupon.times_redeemed ?? 0;
  const max = coupon.details?.max_redemptions;
  return max == null ? String(used) : `${used}/${max}`;
};

const REGULAR_COUPONS_PREVIEW_COUNT = 3;

const CouponDetailsPanel = ({ coupon, details, formatDiscount, formatPlans, formatBillingPeriods }) => (
  <div className={`${surfaces.secondary} ${borders.default} border rounded-md p-4`}>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="space-y-1">
        <p className={`${typography.caption} ${textColors.muted}`}>Discount</p>
        <p className={`${typography.bodySmall} ${textColors.primary}`}>{formatDiscount(details)}</p>
      </div>
      <div className="space-y-1">
        <p className={`${typography.caption} ${textColors.muted}`}>Status</p>
        <p className={`${typography.bodySmall} ${textColors.primary}`}>
          {coupon.is_effectively_active ? "Active" : "Inactive"}
        </p>
      </div>
      <div className="space-y-1">
        <p className={`${typography.caption} ${textColors.muted}`}>Includes renewals</p>
        <p className={`${typography.bodySmall} ${textColors.primary}`}>
          {details.include_renewals ? "Yes" : "No"}
        </p>
      </div>
      <div className="space-y-1">
        <p className={`${typography.caption} ${textColors.muted}`}>Subscription type</p>
        <p className={`${typography.bodySmall} ${textColors.primary}`}>{formatPlans(details)}</p>
      </div>
      <div className="space-y-1">
        <p className={`${typography.caption} ${textColors.muted}`}>Billing period</p>
        <p className={`${typography.bodySmall} ${textColors.primary}`}>{formatBillingPeriods(details)}</p>
      </div>
      <div className="space-y-1">
        <p className={`${typography.caption} ${textColors.muted}`}>First-time only</p>
        <p className={`${typography.bodySmall} ${textColors.primary}`}>
          {details.first_time_only ? "Yes" : "No"}
        </p>
      </div>
      <div className="space-y-1">
        <p className={`${typography.caption} ${textColors.muted}`}>Max redemptions</p>
        <p className={`${typography.bodySmall} ${textColors.primary}`}>
          {details.max_redemptions == null ? "Unlimited" : details.max_redemptions}
        </p>
      </div>
      {formatFreeAccess(details) ? (
        <div className="space-y-1">
          <p className={`${typography.caption} ${textColors.muted}`}>Access</p>
          <p className={`${typography.bodySmall} ${textColors.primary}`}>{formatFreeAccess(details)}</p>
        </div>
      ) : null}
    </div>
  </div>
);

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(min-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(media.matches);
    onChange();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  return isDesktop;
};

const MyCouponsPage = () => {
  const [expandedCouponIds, setExpandedCouponIds] = useState(() => new Set());
  const [hasInitializedExpand, setHasInitializedExpand] = useState(false);
  const [showAllRegular, setShowAllRegular] = useState(false);
  const [copyNotice, setCopyNotice] = useState(null); // { type: 'success'|'error', message }
  // At most one inline send panel is open at a time — the coupon id it belongs to.
  const [openSendCouponId, setOpenSendCouponId] = useState(null);
  // One entry per recipient. Multi-usage access coupons may hold several.
  const [sendEmails, setSendEmails] = useState([""]);
  const [sendFeedback, setSendFeedback] = useState(null); // { type: 'success'|'error', message }
  const [isSending, setIsSending] = useState(false);
  // Access Coupons table controls. An empty filter set means "no status filter";
  // the status chips are the only control now — a separate "include used" toggle
  // duplicated what deselecting the Used chip already does.
  const [accessStatusFilter, setAccessStatusFilter] = useState(() => new Set());
  // Which coupon's recipient list is expanded (only one at a time).
  const [openRecipientsCouponId, setOpenRecipientsCouponId] = useState(null);
  const isDesktop = useIsDesktop();
  const portalBasePath = usePortalBasePath();
  const { affiliateAuth } = useContext(AffiliateAuthContext) || {};
  // Partners get a reduced view: one "Linked Users" stat, no commission copy,
  // and "Linked" instead of "Affiliated" as the per-coupon column label.
  const isPartner = isPartnerKind(affiliateAuth?.user);
  const linkedColumnLabel = isPartner ? "Linked" : "Affiliated";
  const couponsListLabel = isPartner ? "Partner coupons list" : "Affiliate coupons list";
  const couponsTableLabel = isPartner ? "Partner coupons table" : "Affiliate coupons table";

  const couponsQuery = useAffiliateCoupons();
  const sendAccessCoupons = useSendAccessCoupons();
  const sendRegularCoupon = useSendRegularCoupon();
  const totals = couponsQuery.data?.totals || {};
  const allCoupons = couponsQuery.data?.coupons || [];
  // Regular coupons (share links, affiliation stats) vs access coupons (one-time free-access codes).
  const coupons = allCoupons.filter((c) => !c.is_access_coupon);
  const accessCoupons = allCoupons.filter((c) => c.is_access_coupon);
  // Used coupons always sort last; the toggle can drop them entirely and the chips
  // narrow to specific statuses.
  const visibleAccessCoupons = useMemo(
    () =>
      sortCouponsByStatus(
        filterCoupons(accessCoupons, { statuses: accessStatusFilter })
      ),
    [accessCoupons, accessStatusFilter]
  );
  const hiddenRegularCount = Math.max(0, coupons.length - REGULAR_COUPONS_PREVIEW_COUNT);
  const visibleCoupons =
    showAllRegular || hiddenRegularCount === 0
      ? coupons
      : coupons.slice(0, REGULAR_COUPONS_PREVIEW_COUNT);

  const affiliateUsers = totals.affiliate_users ?? 0;
  const payingAffiliateUsers = totals.paying_affiliate_users ?? 0;
  const newPayingThisMonth = totals.new_paying_this_month ?? 0;
  const currentMonthName = new Date().toLocaleString("en-US", { month: "long" });
  const commissionTierProgress = getCommissionTierProgressParts(newPayingThisMonth);

  useEffect(() => {
    if (!copyNotice) return undefined;
    const timer = window.setTimeout(() => setCopyNotice(null), 2200);
    return () => window.clearTimeout(timer);
  }, [copyNotice]);

  // On desktop only: with few coupons, open details by default.
  // Keep rows collapsed on mobile to save vertical space.
  useEffect(() => {
    if (hasInitializedExpand || couponsQuery.isLoading) return;
    if (isDesktop && coupons.length > 0 && coupons.length <= 2) {
      setExpandedCouponIds(new Set(coupons.map((c) => c.id)));
    }
    setHasInitializedExpand(true);
  }, [coupons, couponsQuery.isLoading, hasInitializedExpand, isDesktop]);

  const couponShareLink = (code) => {
    if (!code || typeof window === "undefined") return null;
    return `${window.location.origin}/discount/${encodeURIComponent(code)}`;
  };

  const primaryShareLink = coupons.length ? couponShareLink(coupons[0].code) : null;
  const primaryCouponCode = coupons.length ? coupons[0].code : null;

  const copyToClipboard = async (value, successMessage) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        setCopyNotice({ type: "success", message: successMessage });
      } else {
        throw new Error("Clipboard not available");
      }
    } catch (_e) {
      setCopyNotice({ type: "error", message: "Could not copy automatically. Please copy manually." });
    }
  };

  const toggleExpanded = (couponId) => {
    setExpandedCouponIds((prev) => {
      const next = new Set(prev);
      if (next.has(couponId)) next.delete(couponId);
      else next.add(couponId);
      return next;
    });
  };

  // Opening a panel always resets the typed email and any feedback, so one
  // coupon's in-progress send never leaks into another coupon's panel.
  const toggleSendPanel = (coupon) => {
    if (isSending) return;
    setSendEmails([""]);
    setSendFeedback(null);
    setOpenSendCouponId((prev) => (prev === coupon.id ? null : coupon.id));
  };

  const updateSendEmail = (index, value) =>
    setSendEmails((prev) => prev.map((entry, i) => (i === index ? value : entry)));

  const addSendEmail = () => setSendEmails((prev) => [...prev, ""]);

  const removeSendEmail = (index) =>
    setSendEmails((prev) => (prev.length <= 1 ? prev : prev.filter((_entry, i) => i !== index)));

  const readSendErrorMessage = (error, fallback) =>
    error?.data?.error || error?.data?.detail || error?.message || fallback;

  const submitSend = async (event, coupon) => {
    event.preventDefault();
    if (isSending) return;

    // Blank rows are ignored so an unused "+ Add another" box cannot block a send.
    const addresses = sendEmails.map((entry) => entry.trim()).filter(Boolean);
    if (addresses.length === 0 || !addresses.every(isValidEmail)) {
      setSendFeedback({
        type: "error",
        message:
          addresses.length > 1
            ? "Enter a valid email address in every filled box."
            : "Enter a valid email address.",
      });
      return;
    }
    const uniqueAddresses = Array.from(new Set(addresses.map((a) => a.toLowerCase())));
    if (uniqueAddresses.length !== addresses.length) {
      setSendFeedback({ type: "error", message: "The same address is listed more than once." });
      return;
    }

    setIsSending(true);
    setSendFeedback(null);
    try {
      if (coupon.is_access_coupon) {
        // The bulk endpoint takes a recipients list — one entry per address, each
        // getting this one coupon.
        const data = await sendAccessCoupons.mutateAsync(
          uniqueAddresses.map((email) => ({ email, coupon_ids: [coupon.id] }))
        );
        // Access sends always return HTTP 200: per-recipient outcomes live in
        // `results`, so a "sent" response can still mean nothing was delivered.
        const results = Array.isArray(data?.results) ? data.results : [];
        const failed = results.filter((result) => result?.status !== "sent");
        if (failed.length > 0) {
          const detail = failed
            .map((result) => `${result.email}: ${result.error || result.status}`)
            .join("; ");
          setSendFeedback({
            type: "error",
            message:
              failed.length === results.length
                ? `Could not send ${coupon.code} — ${detail}`
                : `Sent ${results.length - failed.length} of ${results.length}. Failed — ${detail}`,
          });
          return;
        }
        setSendFeedback({
          type: "success",
          message: `Coupon ${coupon.code} sent to ${uniqueAddresses.join(", ")}.`,
        });
      } else {
        const [email] = uniqueAddresses;
        await sendRegularCoupon.mutateAsync({ email, couponId: coupon.id });
        setSendFeedback({ type: "success", message: `Coupon ${coupon.code} sent to ${email}.` });
      }
      setSendEmails([""]);
    } catch (error) {
      setSendFeedback({
        type: "error",
        message: readSendErrorMessage(error, "Could not send this coupon. Please try again."),
      });
    } finally {
      setIsSending(false);
    }
  };

  const toggleAccessStatusFilter = (statusValue) =>
    setAccessStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(statusValue)) next.delete(statusValue);
      else next.add(statusValue);
      return next;
    });

  // A used-up coupon has nothing left to send, so it gets no send action at all.
  // One that has already gone out offers "Send again" rather than a first send.
  const canSendCoupon = (coupon) => !isUsedStatus(coupon.status);
  const sendLabelFor = (coupon) =>
    coupon.status === COUPON_STATUS_SENT || Number(coupon.sent_count || 0) > 0
      ? "Send again"
      : "Send by email";

  const isSendPanelOpen = (coupon) => openSendCouponId === coupon.id;

  const renderSendPanel = (coupon) =>
    isSendPanelOpen(coupon) ? (
      <CouponSendPanel
        coupon={coupon}
        emails={sendEmails}
        onEmailChange={updateSendEmail}
        onAddEmail={addSendEmail}
        onRemoveEmail={removeSendEmail}
        onSubmit={(event) => submitSend(event, coupon)}
        isSending={isSending}
        feedback={sendFeedback}
      />
    ) : null;

  const formatDiscount = (details) => {
    if (!details) return "—";
    if (details.discount_type === "percent") return `${details.percent_off}% off`;
    if (details.discount_type === "amount") return `${details.amount_off} ${details.currency} off`;
    if (details.discount_type === "trial") return `${details.free_trial_days || 0} trial days`;
    return "—";
  };

  const formatPlans = (details) => {
    const plans = details?.plans || [];
    if (!plans.length) return "All plans";
    return plans.join(", ");
  };

  const formatBillingPeriods = (details) => {
    const periods = details?.billing_periods || [];
    if (!periods.length) return "All billing periods";
    return periods.join(", ");
  };

  return (
    <div className="p-4 sm:p-6">
      {copyNotice && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed top-4 right-4 z-[100] flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg max-w-sm ${
            copyNotice.type === "success"
              ? "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800"
              : "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800"
          }`}
        >
          <span
            className={`${typography.bodySmall} ${
              copyNotice.type === "success"
                ? "text-green-800 dark:text-green-300"
                : "text-red-800 dark:text-red-300"
            }`}
          >
            {copyNotice.message}
          </span>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className={`${typography.h2} ${textColors.primary}`}>My Coupons</h1>
          <p className={`${typography.body} ${textColors.secondary} mt-1`}>
            Track signups and paid conversions from your coupon links.
          </p>
        </div>

        {isPartner ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label="Linked Users"
              value={affiliateUsers}
              hint="People who signed up through your link or coupon"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label="Affiliate Users"
              value={affiliateUsers}
              hint="People who signed up through your link or coupon"
            />
            <StatCard
              label="Paying Affiliate Users"
              value={payingAffiliateUsers}
              hint="Referred users who have made a paid purchase"
            />
            <StatCard
              label={`New Paying in ${currentMonthName}`}
              value={newPayingThisMonth}
              footer={
                <>
                  {commissionTierProgress.newLabel} ={" "}
                  <span className={affiliateBrandGradientText}>{commissionTierProgress.commissionLabel}</span>
                </>
              }
              hint={`Your commission rate in ${currentMonthName} is based on how many new paying referrals you bring in ${currentMonthName}.`}
            />
          </div>
        )}

        <Card>
          <CardHeader title="Coupons" />
          <CardBody className="space-y-4">
            {couponsQuery.isLoading && <p className={`${typography.bodySmall} ${textColors.secondary}`}>Loading…</p>}
            {couponsQuery.error && (
              <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                Failed to load: {couponsQuery.error?.message || "Unknown error"}
              </p>
            )}

            {!couponsQuery.isLoading && !couponsQuery.error && coupons.length === 0 && (
              <p className={`${typography.bodySmall} ${textColors.secondary}`}>No coupons linked.</p>
            )}

            {coupons.length > 0 && !isDesktop && (
              <div className="space-y-3" aria-label={couponsListLabel}>
                {visibleCoupons.map((c) => {
                  const isOpen = expandedCouponIds.has(c.id);
                  const details = c.details || {};
                  const shareLink = couponShareLink(c.code);
                  return (
                    <div
                      key={c.id}
                      className={`${surfaces.primary} rounded-xl border ${borders.default} overflow-hidden`}
                    >
                      <button
                        type="button"
                        className={`w-full text-left px-4 py-3 ${surfaces.hover}`}
                        onClick={() => toggleExpanded(c.id)}
                        aria-expanded={isOpen}
                      >
                        <div className="flex items-start gap-2.5">
                          <ExpandChevron isOpen={isOpen} />
                          <div className="min-w-0 flex-1">
                            <p className={`${typography.body} ${textColors.primary} font-medium break-all`}>
                              {c.code}
                            </p>
                            <p className={`${typography.caption} ${textColors.muted} mt-0.5`}>
                              {isOpen ? "Hide details" : "Show details"}
                            </p>
                          </div>
                        </div>
                      </button>

                      <div className={`px-4 pb-4 space-y-3 border-t ${borders.default}`}>
                        <div className="grid grid-cols-2 gap-3 pt-3">
                          <div>
                            <p className={`${typography.caption} ${textColors.muted}`}>{linkedColumnLabel}</p>
                            <p className={`${typography.body} ${textColors.primary}`}>{c.affiliated ?? 0}</p>
                          </div>
                          <div>
                            <p className={`${typography.caption} ${textColors.muted}`}>Paying</p>
                            <p className={`${typography.body} ${textColors.primary}`}>{c.paying ?? 0}</p>
                          </div>
                        </div>

                        {shareLink && (
                          <div className="space-y-1">
                            <p className={`${typography.caption} ${textColors.muted}`}>Link</p>
                            <a
                              href={shareLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${typography.bodySmall} ${textColors.primary} underline break-all`}
                            >
                              {shareLink}
                            </a>
                          </div>
                        )}

                        <CouponActions
                          code={c.code}
                          shareLink={shareLink}
                          onCopy={copyToClipboard}
                          fullWidth
                          onToggleSend={() => toggleSendPanel(c)}
                          isSendOpen={isSendPanelOpen(c)}
                        />

                        {renderSendPanel(c)}

                        {isOpen && (
                          <CouponDetailsPanel
                            coupon={c}
                            details={details}
                            formatDiscount={formatDiscount}
                            formatPlans={formatPlans}
                            formatBillingPeriods={formatBillingPeriods}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {coupons.length > 0 && isDesktop && (
              <div className={`${surfaces.primary} rounded-lg overflow-hidden ${borders.default} border`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full w-full" aria-label={couponsTableLabel}>
                    <thead className={`${surfaces.secondary} ${borders.default} border-b`}>
                      <tr>
                        <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>Code</th>
                        <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>Link</th>
                        <th className={`px-4 py-3 text-right ${typography.label} ${textColors.secondary}`}>
                          {linkedColumnLabel}
                        </th>
                        <th className={`px-4 py-3 text-right ${typography.label} ${textColors.secondary}`}>
                          Paying
                        </th>
                        <th className={`px-4 py-3 text-right ${typography.label} ${textColors.secondary}`}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCoupons.map((c) => {
                        const isOpen = expandedCouponIds.has(c.id);
                        const details = c.details || {};
                        const shareLink = couponShareLink(c.code);
                        return (
                          <React.Fragment key={c.id}>
                            <tr
                              className={`${borders.default} border-b ${surfaces.hover} cursor-pointer`}
                              onClick={() => toggleExpanded(c.id)}
                              aria-expanded={isOpen}
                            >
                              <td className={`px-4 py-3 ${typography.body} ${textColors.primary} font-medium`}>
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <ExpandChevron isOpen={isOpen} />
                                  <span className="truncate">{c.code}</span>
                                  <span className={`${typography.caption} ${textColors.muted}`}>
                                    {isOpen ? "Hide details" : "Show details"}
                                  </span>
                                </div>
                              </td>
                              <td
                                className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary} max-w-xs`}
                              >
                                {shareLink ? (
                                  <span className="block truncate" title={shareLink}>
                                    {shareLink}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className={`px-4 py-3 ${typography.body} ${textColors.primary} text-right`}>
                                {c.affiliated ?? 0}
                              </td>
                              <td className={`px-4 py-3 ${typography.body} ${textColors.primary} text-right`}>
                                {c.paying ?? 0}
                              </td>
                              <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                <CouponActions
                                  code={c.code}
                                  shareLink={shareLink}
                                  onCopy={copyToClipboard}
                                  onToggleSend={() => toggleSendPanel(c)}
                                  isSendOpen={isSendPanelOpen(c)}
                                />
                              </td>
                            </tr>

                            {isSendPanelOpen(c) && (
                              <tr className={`${borders.default} border-b`}>
                                <td colSpan={5} className="px-4 py-4">
                                  {renderSendPanel(c)}
                                </td>
                              </tr>
                            )}

                            {isOpen && (
                              <tr className={`${borders.default} border-b`}>
                                <td colSpan={5} className="px-4 py-4">
                                  <CouponDetailsPanel
                                    coupon={c}
                                    details={details}
                                    formatDiscount={formatDiscount}
                                    formatPlans={formatPlans}
                                    formatBillingPeriods={formatBillingPeriods}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {hiddenRegularCount > 0 && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowAllRegular((prev) => !prev)}
                  aria-expanded={showAllRegular}
                  className={`px-4 py-2 rounded-lg border ${borders.default} ${surfaces.secondary} ${surfaces.hover} ${typography.bodySmall} ${textColors.primary} font-medium`}
                >
                  {showAllRegular ? "Show less" : `Show more (${hiddenRegularCount})`}
                </button>
              </div>
            )}

            <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-4 sm:p-5 space-y-3`}>
              <h3 className={`${typography.h5} ${textColors.primary}`}>How your link and coupon work</h3>
              <div className={`space-y-2 ${typography.bodySmall} ${textColors.secondary}`}>
                {primaryShareLink ? (
                  <p>
                    Share your personal link:{" "}
                    <a
                      href={primaryShareLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${textColors.primary} font-medium underline break-all`}
                    >
                      {primaryShareLink}
                    </a>
                    {coupons.length > 1 ? <span> (or any of your other coupon links above)</span> : null}. When
                    someone creates an account through that link, they are affiliated with you right away. From
                    then on, your coupon is applied automatically when they choose a plan later.
                  </p>
                ) : (
                  <p>
                    Share your personal coupon link from the list above. When someone creates an account through
                    that link, they are affiliated with you right away. From then on, your coupon is applied
                    automatically when they choose a plan later.
                  </p>
                )}
                <p>
                  People can also enter your coupon code
                  {primaryCouponCode ? (
                    <>
                      {" "}
                      (<span className={`${textColors.primary} font-medium`}>{primaryCouponCode}</span>)
                    </>
                  ) : null}{" "}
                  at checkout. Applying the code when they pay affiliates them to you and unlocks the discount on
                  that purchase.
                </p>
                <p>
                  E-mail{" "}
                  <a
                    href="mailto:contact@myvitae.ai"
                    className={`${textColors.primary} font-medium underline`}
                  >
                    contact@myvitae.ai
                  </a>{" "}
                  if you have questions or need a new coupon.
                </p>
                <p className={`${typography.caption} ${textColors.muted}`}>
                  Tip: the link is usually easier to share. The code is handy when someone is already on the
                  payment page.
                </p>
              </div>
            </div>

            <p className={`${typography.caption} ${textColors.muted}`}>
              No user identities are shown anywhere in this portal.
            </p>
          </CardBody>
        </Card>

        {accessCoupons.length > 0 && (
          <Card>
            <CardHeader
              title="Access Coupons"
              actions={
                <Link
                  to={`${portalBasePath}/dashboard/send-access-coupons`}
                  className={`${typography.bodySmall} ${textColors.secondary} underline underline-offset-2 hover:brightness-110 whitespace-nowrap`}
                >
                  Send multiple by e-mail
                </Link>
              }
            />
            <CardBody className="space-y-4">
              <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                One-time codes that give the person who redeems them free access to a plan.
                {/* Only affiliates earn commission — partners have no payments tab. */}
                {isPartner ? null : " If the person later starts a paid plan, you get commissions on it!"}
              </p>

              {/* Status filter + used toggle. Used coupons always sort last, and can
                  be hidden entirely so a long history does not bury live codes. */}
              <div className="flex flex-wrap items-center gap-2">
                <span className={`${typography.label} ${textColors.secondary}`}>Status</span>
                {COUPON_STATUS_FILTER_OPTIONS.map((statusValue) => {
                  const isSelected = accessStatusFilter.has(statusValue);
                  return (
                    <button
                      key={statusValue}
                      type="button"
                      onClick={() => toggleAccessStatusFilter(statusValue)}
                      aria-pressed={isSelected}
                      className={[
                        "px-2.5 py-1 rounded-full border",
                        typography.caption,
                        isSelected
                          ? COUPON_STATUS_BADGE_CLASSES[statusValue]
                          : `${surfaces.glass} ${borders.glass} ${textColors.secondary}`,
                      ].join(" ")}
                    >
                      {couponStatusLabel(statusValue)}
                    </button>
                  );
                })}
              </div>

              {visibleAccessCoupons.length === 0 ? (
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                  No access coupons match these filters.
                </p>
              ) : null}

              {!isDesktop && (
                <div className="space-y-3" aria-label="Access coupons list">
                  {visibleAccessCoupons.map((c) => {
                    const details = c.details || {};
                    return (
                      <div
                        key={c.id}
                        className={`${surfaces.primary} rounded-xl border ${borders.default} p-4 space-y-3`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className={`${typography.body} ${textColors.primary} font-medium break-all`}>
                            {c.code}
                          </p>
                          <span
                            // Labelled because the card also has a "Used" field for
                            // the redemption count — a bare "Used" pill next to it is
                            // ambiguous both visually and to a screen reader.
                            aria-label={`Status: ${couponStatusLabel(c.status)}`}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full whitespace-nowrap ${typography.caption} ${
                              COUPON_STATUS_BADGE_CLASSES[c.status] || ""
                            }`}
                          >
                            {couponStatusLabel(c.status)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className={`${typography.caption} ${textColors.muted}`}>Used</p>
                            <p className={`${typography.body} ${textColors.primary}`}>{formatUsed(c)}</p>
                          </div>
                          <div>
                            <p className={`${typography.caption} ${textColors.muted}`}>Access</p>
                            <p className={`${typography.body} ${textColors.primary}`}>
                              {formatFreeAccess(details) || "—"}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className={`${typography.caption} ${textColors.muted}`}>Linked coupon</p>
                            <p className={`${typography.body} ${textColors.primary} break-all`}>
                              {c.linked_coupon_code || "—"}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Sent to</p>
                          <SentRecipientsCell
                            coupon={c}
                            isOpen={openRecipientsCouponId === c.id}
                            onToggle={() =>
                              setOpenRecipientsCouponId((prev) => (prev === c.id ? null : c.id))
                            }
                          />
                        </div>
                        <CouponActions
                          code={c.code}
                          shareLink={null}
                          onCopy={copyToClipboard}
                          fullWidth
                          onToggleSend={canSendCoupon(c) ? () => toggleSendPanel(c) : null}
                          isSendOpen={isSendPanelOpen(c)}
                          sendLabel={sendLabelFor(c)}
                        />
                        {renderSendPanel(c)}
                      </div>
                    );
                  })}
                </div>
              )}

              {isDesktop && (
                <div className={`${surfaces.primary} rounded-lg overflow-hidden ${borders.default} border`}>
                  <div className="overflow-x-auto">
                    <table className="min-w-full w-full" aria-label="Access coupons table">
                      <thead className={`${surfaces.secondary} ${borders.default} border-b`}>
                        <tr>
                          <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                            Code
                          </th>
                          <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                            Status
                          </th>
                          <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                            Used
                          </th>
                          <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                            Access
                          </th>
                          <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                            Linked coupon
                          </th>
                          <th className={`px-4 py-3 text-left ${typography.label} ${textColors.secondary}`}>
                            Sent to
                          </th>
                          <th className={`px-4 py-3 text-right ${typography.label} ${textColors.secondary}`}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleAccessCoupons.map((c) => {
                          const details = c.details || {};
                          return (
                            <React.Fragment key={c.id}>
                            <tr className={`${borders.default} border-b`}>
                              <td className={`px-4 py-3 ${typography.body} ${textColors.primary} font-medium`}>
                                <span className="break-all">{c.code}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  aria-label={`Status: ${couponStatusLabel(c.status)}`}
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full whitespace-nowrap ${typography.caption} ${
                                    COUPON_STATUS_BADGE_CLASSES[c.status] || ""
                                  }`}
                                >
                                  {couponStatusLabel(c.status)}
                                </span>
                              </td>
                              <td className={`px-4 py-3 ${typography.body} ${textColors.primary}`}>
                                {formatUsed(c)}
                              </td>
                              <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.primary}`}>
                                {formatFreeAccess(details) || "—"}
                              </td>
                              <td className={`px-4 py-3 ${typography.bodySmall} ${textColors.secondary}`}>
                                {c.linked_coupon_code || "—"}
                              </td>
                              <td className="px-4 py-3 align-top">
                                <SentRecipientsCell
                                  coupon={c}
                                  isOpen={openRecipientsCouponId === c.id}
                                  onToggle={() =>
                                    setOpenRecipientsCouponId((prev) => (prev === c.id ? null : c.id))
                                  }
                                />
                              </td>
                              <td className="px-4 py-3 text-right">
                                <CouponActions
                                  code={c.code}
                                  shareLink={null}
                                  onCopy={copyToClipboard}
                                  onToggleSend={canSendCoupon(c) ? () => toggleSendPanel(c) : null}
                                  isSendOpen={isSendPanelOpen(c)}
                                  sendLabel={sendLabelFor(c)}
                                />
                              </td>
                            </tr>
                            {isSendPanelOpen(c) && (
                              <tr className={`${borders.default} border-b`}>
                                <td colSpan={7} className="px-4 py-4">
                                  {renderSendPanel(c)}
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MyCouponsPage;
